import React from 'react';

import {useI18n, useFormatNumber, LocalizedHtml} from '@udemy/i18n';
import {QuestionAndAnswer, QuestionsAndAnswers} from '@udemy/react-frequently-asked-questions';
import {
    FALLBACK_COLLECTION_STATS,
    usePersonalPlanCollectionStats,
} from '@udemy/subscription-browse';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';
import {useUDData, getNumericSiteStat, udLink} from '@udemy/ud-data';

import {shouldShowMonthlyPlanFaqCopy} from '../../utils/personal-plan-renewal-interval';
import styles from './personal-plan-faq.module.less';

export const PersonalPlanFAQ = ({
    isPPLPCompleteRevamp = false,
}: {
    isPPLPCompleteRevamp?: boolean;
}) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const consumerSubscriptionPlan = useConsumerSubscriptionPlan();
    const unwrappedConsumerSubscriptionPlan =
        consumerSubscriptionPlan && 'id' in consumerSubscriptionPlan
            ? consumerSubscriptionPlan
            : undefined;

    const udData = useUDData();
    const personalPlanCollectionStats = usePersonalPlanCollectionStats();
    const collectionStats =
        personalPlanCollectionStats && 'numberOfCourses' in personalPlanCollectionStats
            ? personalPlanCollectionStats
            : FALLBACK_COLLECTION_STATS;

    // Check if trial data is available
    const planHasTrial = !!unwrappedConsumerSubscriptionPlan?.trialPeriodDays;
    const TEMP_PLAN_HAS_TRIAL = true; // Temporary variable to allow us to integrate the new translations without showing un-translated copy. This will be removed once translations are done.
    const trialPeriodDays = unwrappedConsumerSubscriptionPlan?.trialPeriodDays;
    const isAnnualPlanEnabled = unwrappedConsumerSubscriptionPlan?.isAnnualPlanEnabled;
    const shouldShowMonthlyPlanCopy = shouldShowMonthlyPlanFaqCopy(
        unwrappedConsumerSubscriptionPlan,
    );

    // Get site stats
    const totalCoursesCount = getNumericSiteStat(
        udData,
        udData.request.locale,
        'num_courses_rounded',
    );
    const globalLearnersCount = getNumericSiteStat(
        udData,
        udData.request.locale,
        'num_instructors',
    );
    const personalPlanDescription = shouldShowMonthlyPlanCopy
        ? gettext(
              'Personal Plan is a monthly subscription that gives you access to a curated collection of %(ppCourseCount)s top courses. The collection features courses on in-demand professional topics — including web development, IT certification, data science, web design, digital marketing, and leadership — along with a selection of personal development topics, such as language learning, arts and creativity, and personal finance.',
          )
        : gettext(
              'Personal Plan is a subscription that gives you access to a curated collection of %(ppCourseCount)s top courses. The collection features courses on in-demand professional topics — including web development, IT certification, data science, web design, digital marketing, and leadership — along with a selection of personal development topics, such as language learning, arts and creativity, and personal finance.',
          );
    const personalPlanCourseAccessDescription = shouldShowMonthlyPlanCopy
        ? gettext(
              'With Personal Plan you gain monthly access to over %(ppCourseCount)s of our top-rated courses and practice activities in top tech, business and personal categories. When you purchase a single Udemy course, you get lifetime access to that course and course material only.',
          )
        : gettext(
              'With Personal Plan you gain access to over %(ppCourseCount)s of our top-rated courses and practice activities in top tech, business and personal categories. When you purchase a single Udemy course, you get lifetime access to that course and course material only.',
          );

    const revampPersonalPlanDescription = shouldShowMonthlyPlanCopy
        ? gettext(
              'Personal Plan is a monthly subscription that helps you build practical skills and achieve your professional and personal goals. Explore a curated collection of over %(ppCourseCount)s top courses in AI, web development, IT certification, data science, digital marketing, languages, leadership, and more, with Skill Assessments and hands-on practice to apply what you learn. Move freely between courses and instructors, without being tied to a single course, to find the content and teaching styles that work best for you.',
          )
        : gettext(
              'Personal Plan is a subscription that helps you build practical skills and achieve your professional and personal goals. Explore a curated collection of over %(ppCourseCount)s top courses in AI, web development, IT certification, data science, digital marketing, languages, leadership, and more, with Skill Assessments and hands-on practice to apply what you learn. Move freely between courses and instructors, without being tied to a single course, to find the content and teaching styles that work best for you.',
          );

    const faq: (QuestionAndAnswer & {hidden?: boolean})[] = [
        {
            question: gettext('What is Personal Plan?'),
            answer: interpolate(
                personalPlanDescription,
                {
                    ppCourseCount: formatNumber(collectionStats.numberOfCourses),
                },
                true,
            ),
        },
        {
            question: gettext('How is Personal Plan different from buying a course?'),
            answer: interpolate(
                personalPlanCourseAccessDescription,
                {
                    ppCourseCount: formatNumber(collectionStats.numberOfCourses),
                },
                true,
            ),
        },
        {
            question: gettext('How are courses selected for Personal Plan?'),
            answer: interpolate(
                gettext(
                    "The %(ppCourseCount)s courses included in Personal Plan are curated by Udemy's content experts from our catalog of %(courseCount)s courses. We use insights from %(learnerCount)s global learners to identify top-rated, relevant courses on the most sought-after professional topics as well as a selection of personal development topics. Personal Plan gives you easy access to fresh, high-quality courses on the latest skills.",
                ),
                {
                    ppCourseCount: formatNumber(collectionStats.numberOfCourses),
                    courseCount: totalCoursesCount,
                    learnerCount: globalLearnersCount,
                },
                true,
            ),
        },
        {
            question: gettext('How does the free trial work?'),
            answer: (
                <LocalizedHtml
                    html={interpolate(
                        isAnnualPlanEnabled
                            ? gettext(
                                  'Your subscription may start with a free trial. If you qualify for a free trial offer, you can try Personal Plan free for %(trialPeriodDays)s days to see if it suits your needs. After %(trialPeriodDays)s days, the trial will automatically convert to an annual subscription with a cost of %(listPriceText)s per year (plus <a class="transactionTaxesSupportUrl">applicable transaction taxes</a>). You can cancel the %(trialPeriodDays)s-day free trial at any time at no charge. Head to the Subscriptions tab in your account settings to <a class="subscriptionSupportUrl">manage your Udemy subscription</a>.<br/>' +
                                      'Note: Free trial offers are only available to new subscribers. If you have previously accessed the Personal Plan, either through a free trial or a paid subscription, you are not eligible for the free trial. Free trial offers are not available in certain jurisdictions.',
                              )
                            : gettext(
                                  'Your subscription may start with a free trial. If you qualify for a free trial offer, you can try Personal Plan free for %(trialPeriodDays)s days to see if it suits your needs. After %(trialPeriodDays)s days, the trial will automatically convert to a monthly subscription with a cost of %(listPriceText)s per month (plus <a class="transactionTaxesSupportUrl">applicable transaction taxes</a>). You can cancel the %(trialPeriodDays)s-day free trial at any time at no charge. Head to the Subscriptions tab in your account settings to <a class="subscriptionSupportUrl">manage your Udemy subscription</a>.<br/>' +
                                      'Note: Free trial offers are only available to new subscribers. If you have previously accessed the Personal Plan, either through a free trial or a paid subscription, you are not eligible for the free trial. Free trial offers are not available in certain jurisdictions.',
                              ),
                        {
                            trialPeriodDays,
                            listPriceText: unwrappedConsumerSubscriptionPlan?.formattedListPrice,
                        },
                        true,
                    )}
                    interpolate={{
                        transactionTaxesSupportUrl: (
                            // eslint-disable-next-line
                            <a
                                data-purpose="applicable-transaction-taxes-url"
                                href={udLink.toSupportLink('student_taxes')}
                            />
                        ),
                        subscriptionSupportUrl: (
                            // eslint-disable-next-line
                            <a
                                data-purpose="manage-subscription-url"
                                href={udLink.toSupportLink('manage_subscription')}
                            />
                        ),
                    }}
                />
            ),
            hidden: !planHasTrial,
        },
        {
            question: gettext('How and when will I be billed?'),
            answer: !TEMP_PLAN_HAS_TRIAL
                ? gettext(
                      'If you\'re signing up for a paid subscription, the subscription fee for your first billing cycle will be charged immediately after signing up for Personal Plan. You will be billed your subscription fee plus applicable transaction taxes on the same day each year. You can view your billing date on your <a data-purpose="subs-page-link" href="/user/manage-subscriptions/">Subscriptions page</a>.',
                  )
                : gettext(
                      'If your subscription begins with a free trial offer, you will be charged the subscription fee for your first billing cycle at the end of your trial. If you\'re signing up for a paid subscription, the subscription fee for your first billing cycle will be charged immediately after signing up for Personal Plan. You will be billed your subscription fee plus applicable transaction taxes on the same day each year. You can view your billing date on your <a data-purpose="subs-page-link" href="/user/manage-subscriptions/">Subscriptions page</a>.',
                  ),
            hidden: !unwrappedConsumerSubscriptionPlan,
        },
        {
            question: gettext('How can I cancel my subscription?'),
            answer: (
                <LocalizedHtml
                    html={
                        !TEMP_PLAN_HAS_TRIAL
                            ? gettext(
                                  'If you decide the subscription isn\'t the right fit for you, you can <a class="manageSubscriptionUrl">manage or cancel your subscription</a> at any time.',
                              )
                            : gettext(
                                  'If you decide the subscription isn\'t the right fit for you, you can <a class="manageSubscriptionUrl">manage or cancel your trial or subscription</a> at any time.',
                              )
                    }
                    interpolate={{
                        manageSubscriptionUrl: (
                            // eslint-disable-next-line
                            <a
                                data-purpose="manage-subscription-url"
                                href="https://support.udemy.com/hc/en-us/articles/1500002916481"
                            />
                        ),
                    }}
                />
            ),
        },
        {
            question: gettext('Are all Udemy courses included in Personal Plan?'),
            answer: interpolate(
                gettext(
                    "No. The %(coursesCount)s courses in personal plan are our highest rated courses in tech, business, and personal development, and have been curated specifically to help you advance in your career. You can access all of the in-demand courses within Personal Plan while you're subscribed, and still purchase courses outside of the subscription at any time.",
                ),
                {coursesCount: formatNumber(collectionStats.numberOfCourses)},
                true,
            ),
        },
    ];

    // Treatment copy for the pplp_complete_revamp experiment. Kept as a standalone copy of
    // `faq` so the control list stays untouched; easier for cleanup as well
    const revampFaq: (QuestionAndAnswer & {hidden?: boolean})[] = [
        {
            question: gettext('What is Personal Plan?'),
            answer: interpolate(
                revampPersonalPlanDescription,
                {
                    ppCourseCount: formatNumber(collectionStats.numberOfCourses),
                },
                true,
            ),
        },
        {
            question: gettext('How is Personal Plan different from buying a course?'),
            answer: interpolate(
                personalPlanCourseAccessDescription,
                {
                    ppCourseCount: formatNumber(collectionStats.numberOfCourses),
                },
                true,
            ),
        },
        {
            question: gettext('What courses are included in Personal Plan?'),
            answer: (
                <LocalizedHtml
                    html={gettext(
                        'For a list of courses available on Personal Plan, please access <a class="personalPlanCoursesListUrl">this link</a>.',
                    )}
                    interpolate={{
                        personalPlanCoursesListUrl: (
                            // eslint-disable-next-line
                            <a
                                data-purpose="personal-plan-courses-list-url"
                                href="https://docs.google.com/spreadsheets/d/1nrh3gPQlA6KLpn_2xuepRnUBNEkJPruU-kP4rLn6Geg/edit?gid=0#gid=0"
                                target="_blank"
                                rel="noopener noreferrer"
                            />
                        ),
                    }}
                />
            ),
        },
        {
            question: gettext('How does the free trial work?'),
            answer: (
                <LocalizedHtml
                    html={interpolate(
                        isAnnualPlanEnabled
                            ? gettext(
                                  'Your subscription may start with a free trial. If you qualify for a free trial offer, you can try Personal Plan free for %(trialPeriodDays)s days to see if it suits your needs. After %(trialPeriodDays)s days, the trial will automatically convert to an annual subscription with a cost of %(listPriceText)s per year (plus <a class="transactionTaxesSupportUrl">applicable transaction taxes</a>). You can cancel the %(trialPeriodDays)s-day free trial at any time at no charge. Head to the Subscriptions tab in your account settings to <a class="subscriptionSupportUrl">manage your Udemy subscription</a>.<br/>' +
                                      'Note: Free trial offers are only available to new subscribers. If you have previously accessed the Personal Plan, either through a free trial or a paid subscription, you are not eligible for the free trial. Free trial offers are not available in certain jurisdictions.',
                              )
                            : gettext(
                                  'Your subscription may start with a free trial. If you qualify for a free trial offer, you can try Personal Plan free for %(trialPeriodDays)s days to see if it suits your needs. After %(trialPeriodDays)s days, the trial will automatically convert to a monthly subscription with a cost of %(listPriceText)s per month (plus <a class="transactionTaxesSupportUrl">applicable transaction taxes</a>). You can cancel the %(trialPeriodDays)s-day free trial at any time at no charge. Head to the Subscriptions tab in your account settings to <a class="subscriptionSupportUrl">manage your Udemy subscription</a>.<br/>' +
                                      'Note: Free trial offers are only available to new subscribers. If you have previously accessed the Personal Plan, either through a free trial or a paid subscription, you are not eligible for the free trial. Free trial offers are not available in certain jurisdictions.',
                              ),
                        {
                            trialPeriodDays,
                            listPriceText: unwrappedConsumerSubscriptionPlan?.formattedListPrice,
                        },
                        true,
                    )}
                    interpolate={{
                        transactionTaxesSupportUrl: (
                            // eslint-disable-next-line
                            <a
                                data-purpose="applicable-transaction-taxes-url"
                                href={udLink.toSupportLink('student_taxes')}
                            />
                        ),
                        subscriptionSupportUrl: (
                            // eslint-disable-next-line
                            <a
                                data-purpose="manage-subscription-url"
                                href={udLink.toSupportLink('manage_subscription')}
                            />
                        ),
                    }}
                />
            ),
            hidden: !planHasTrial,
        },
        {
            question: gettext('How and when will I be billed?'),
            answer: !TEMP_PLAN_HAS_TRIAL
                ? gettext(
                      'If you\'re signing up for a paid subscription, the subscription fee for your first billing cycle will be charged immediately after signing up for Personal Plan. You will be billed your subscription fee plus applicable transaction taxes on the same day each year. You can view your billing date on your <a data-purpose="subs-page-link" href="/user/manage-subscriptions/">Subscriptions page</a>.',
                  )
                : gettext(
                      'If your subscription begins with a free trial offer, you will be charged the subscription fee for your first billing cycle at the end of your trial. If you\'re signing up for a paid subscription, the subscription fee for your first billing cycle will be charged immediately after signing up for Personal Plan. You will be billed your subscription fee plus applicable transaction taxes on the same day each year. You can view your billing date on your <a data-purpose="subs-page-link" href="/user/manage-subscriptions/">Subscriptions page</a>.',
                  ),
            hidden: !unwrappedConsumerSubscriptionPlan,
        },
        {
            question: gettext('How can I cancel my subscription?'),
            answer: (
                <LocalizedHtml
                    html={
                        !TEMP_PLAN_HAS_TRIAL
                            ? gettext(
                                  'If you decide the subscription isn\'t the right fit for you, you can <a class="manageSubscriptionUrl">manage or cancel your subscription</a> at any time.',
                              )
                            : gettext(
                                  'If you decide the subscription isn\'t the right fit for you, you can <a class="manageSubscriptionUrl">manage or cancel your trial or subscription</a> at any time.',
                              )
                    }
                    interpolate={{
                        manageSubscriptionUrl: (
                            // eslint-disable-next-line
                            <a
                                data-purpose="manage-subscription-url"
                                href="https://support.udemy.com/hc/en-us/articles/1500002916481"
                            />
                        ),
                    }}
                />
            ),
        },
        {
            question: gettext('Can I reimburse this through my Employer?'),
            answer: gettext(
                "Many companies offer learning and development budgets that may cover Udemy Personal Plan. Check your employer's reimbursement policy and share how Udemy Personal Plan can help you build the skills you need to achieve your professional goals, grow in your role, and succeed throughout your career. We'll provide a receipt for your reimbursement request.",
            ),
        },
    ];

    const displayedFaq = isPPLPCompleteRevamp ? revampFaq : faq;

    return (
        <div className={styles['faq-container']}>
            <QuestionsAndAnswers
                title={gettext('Frequently asked questions')}
                questionsAndAnswers={displayedFaq.filter((f) => !f.hidden)}
                numRowsToShow={'all'}
            />
        </div>
    );
};
